import { createParser, type EventSourceMessage } from 'eventsource-parser';

import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import type { DispatchContext } from './dispatchStreamEvent';
import { dispatchStreamEvent } from './dispatchStreamEvent';
import { readSseChunk } from './readSseChunk';
import { SidekickHttpError } from './sidekickHttp';
import { parseStreamEvent } from './streamEvent';

/** Terminal outcome of the SSE reader pump for a single turn. */
export type PumpResult = 'done' | 'error' | 'reconnect' | 'closed';

/** Params accepted by {@link pumpSseReader}. */
export type PumpSseReaderParams = {
  /** Reader obtained from {@link subscribe} — one reader per response body. */
  reader: ReadableStreamDefaultReader<Uint8Array>;
  /**
   * Optional readiness chunk already read by {@link subscribe}. The
   * pump feeds it before reading more bytes so `:ping` comments or
   * first data frames are not lost.
   */
  initialChunk?: Uint8Array;
  ctx: DispatchContext;
  signal: AbortSignal;
  /**
   * Optional hook invoked after each decoded SSE data frame (excludes
   * comment / keep-alive lines). Used to clear `isReconnecting` on the
   * first post-reopen event.
   */
  onDecodedEvent?: () => void;
};

/**
 * Drains the response body via `eventsource-parser`, decodes each frame
 * with {@link parseStreamEvent}, and dispatches via
 * {@link dispatchStreamEvent}. `eventId` from the JSON envelope is
 * captured into `ctx.lastEventIdRef` on every dispatched frame; SSE
 * wire `id:` fields also update the ref when present.
 */
export const pumpSseReader = async (
  params: PumpSseReaderParams
): Promise<PumpResult> => {
  const { reader, initialChunk, ctx, signal, onDecodedEvent } = params;
  const decoder = new TextDecoder('utf-8');

  const pendingEvents: EventSourceMessage[] = [];
  const parser = createParser({
    onEvent: (event) => {
      pendingEvents.push(event);
    },
  });

  const feedChunk = (value: Uint8Array): void => {
    const chunk = decoder.decode(value, { stream: true });
    try {
      parser.feed(chunk);
    } catch {
      if (import.meta.env.DEV) {
        console.debug('[pumpSseReader] SSE parser feed failed');
      }
      throw new SidekickHttpError(0, {
        code: SYNTHETIC_TRANSPORT_ERROR_CODE,
        message: 'Sidekick SSE: malformed event stream frame',
      });
    }
  };

  const drainPendingEvents = async (): Promise<PumpResult | null> => {
    while (pendingEvents.length > 0) {
      if (ctx.isActive?.() === false) {
        await reader.cancel().catch(() => undefined);
        return 'done';
      }
      const parsedEvent = pendingEvents.shift();
      if (parsedEvent === undefined) {
        continue;
      }
      if (parsedEvent.id !== undefined && parsedEvent.id !== '') {
        ctx.lastEventIdRef.current = parsedEvent.id;
      }
      const decoded = parseStreamEvent(parsedEvent.data);
      if (decoded === null) {
        continue;
      }
      onDecodedEvent?.();
      const result = dispatchStreamEvent(decoded, ctx);
      if (result === 'done') {
        await reader.cancel().catch(() => undefined);
        return 'done';
      }
      if (result === 'error') {
        await reader.cancel().catch(() => undefined);
        return 'error';
      }
      if (result === 'reconnect') {
        await reader.cancel().catch(() => undefined);
        return 'reconnect';
      }
    }
    return null;
  };

  if (initialChunk !== undefined) {
    feedChunk(initialChunk);
    const initialResult = await drainPendingEvents();
    if (initialResult !== null) {
      return initialResult;
    }
  }

  while (true) {
    if (signal.aborted) {
      await reader.cancel().catch(() => undefined);
      return 'closed';
    }
    const { value, done } = await readSseChunk({
      reader,
      signal,
      timeoutMessage: 'Sidekick SSE: stream read timed out',
    });
    if (done) {
      if (!signal.aborted) {
        await reader.cancel().catch(() => undefined);
      }
      return 'closed';
    }
    feedChunk(value);
    const result = await drainPendingEvents();
    if (result !== null) {
      return result;
    }
  }
};
